<template>
    <v-select
        ref="select"
        :value="value"
        :options="options"
        :label="label"
        :placeholder="placeholder"
        :clearable="clearable"
        :filterable="false"
        :append-to-body="appendToBody"
        :disabled="disabled"
        @input="onInput"
        @search="onSearch"
        @open="onOpen"
        @close="$emit('close')"
        @search:blur="$emit('search-blur')"
    >
        <template #option="option">
            <slot name="option" v-bind="option">
                <div>
                    <div>{{ option.name || option.title }}</div>
                    <div class="text-muted-dark small" v-if="option.cas || option.ec">
                        <span v-if="option.cas">CAS: {{ option.cas }}</span>
                        <span v-if="option.cas && option.ec"> · </span>
                        <span v-if="option.ec">EC: {{ option.ec }}</span>
                    </div>
                </div>
            </slot>
        </template>
        <template #selected-option="option">
            <slot name="selected-option" v-bind="option">
                <span>{{ option.name || option.title }}</span>
            </slot>
        </template>
        <template #no-options="{search}">
            <slot name="no-options" :search="search">
                <div class="text-muted small">
                    {{ search && search.length ? noOptionsText : startTypingText }}
                </div>
            </slot>
        </template>
    </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { SubstanceApi } from '@core/api'

export default {
    name: 'SubstanceSelect',
    components: {
        vSelect,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        includeNoSubstance: {
            type: Boolean,
            default: false,
        },
        noSubstanceLabel: {
            type: String,
            default: '',
        },
        searchFn: {
            type: Function,
            default: SubstanceApi.search,
        },
        placeholder: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: 'name',
        },
        minChars: {
            type: Number,
            default: 2,
        },
        debounceMs: {
            type: Number,
            default: 600,
        },
        appendToBody: {
            type: Boolean,
            default: true,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        noOptionsText: {
            type: String,
            default: '',
        },
        startTypingText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            options: [],
            timer: null,
        }
    },
    computed: {
        noSubstanceOption() {
            if (!this.includeNoSubstance) {
                return null
            }
            const label = this.noSubstanceLabel || 'No substance'
            return {
                id: '__no_substance__',
                name: label,
                title: label,
                isNoSubstance: true,
            }
        },
    },
    methods: {
        getBaseOptions(search = '') {
            return !search && this.noSubstanceOption ? [this.noSubstanceOption] : []
        },
        onOpen() {
            this.options = this.getBaseOptions()
            this.$emit('open')
        },
        focusSearchInput() {
            const select = this.$refs.select
            if (!select) {
                return
            }
            if (typeof select.focus === 'function') {
                select.focus()
            }
            if (select.searchEl && typeof select.searchEl.focus === 'function') {
                select.searchEl.focus()
            }
            select.open = true
        },
        onInput(value) {
            this.$emit('input', value)
        },
        onSearch(search, loading) {
            this.$emit('search-term', search)
            if (!search || search.length < this.minChars) {
                this.options = this.getBaseOptions(search)
                loading(false)
                return
            }
            clearTimeout(this.timer)
            loading(true)
            this.timer = setTimeout(() => {
                this.searchFn(search)
                    .then(response => {
                        const data = (response && response.data) || []
                        const items = data.map(item => ({
                            id: item.id,
                            name: item.name || item.title || '',
                            title: item.title,
                            cas: item.cas,
                            ec: item.ec,
                            raw: item,
                        }))
                        this.options = items
                        loading(false)
                    })
                    .catch(() => {
                        this.options = []
                        loading(false)
                    })
            }, this.debounceMs)
        },
    },
}
</script>

<style scoped>
.text-muted-dark {
    color: #6c757d;
}
</style>
